/**
 * PosSecuritySettingsSection · idle-lock timeout configuration.
 *
 * Where it lives: injected into `/pos/settings` beneath the invoice
 * numbering card. Owns a single field today (`idle_lock_seconds`) but
 * expects to grow to hold other terminal security controls as we add
 * them.
 *
 * Server route: `/api/pos/admin/outlets/{outlet_id}/pos-settings`.
 * The `_source` flag ("saved" | "default") drives the badge and the
 * amber "Using default" banner, mirroring the invoice-config card.
 *
 * Sprint 3B correction · restore idle lock as a real control
 * (previously the value was hard-coded at 120 s and the previous
 * agent removed the setting UI as "documented not-configurable" —
 * that was wrong; the actual production default is 30 minutes and
 * tenants need to change it).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";
import { Lock, Info } from "lucide-react";


const MIN_MINUTES = 1;
const MAX_MINUTES = 240;   // 4 hours — server ceiling


export default function PosSecuritySettingsSection({ outletId, outletName }) {
  const { API, getHeaders } = useAuth();
  // `useAuth().API` already includes the `/api` prefix
  // (`AuthContext.js:6`: `const API = ${BACKEND_URL}/api`). Appending
  // another `/api` here would produce `.../api/api/...` and 404.
  const base = `${API}/pos/admin/outlets/${outletId}/pos-settings`;

  const [minutes, setMinutes] = useState(30);
  const [source,  setSource]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");

  const clientError = useMemo(() => {
    const m = Number(minutes);
    if (!Number.isFinite(m) || m < MIN_MINUTES || m > MAX_MINUTES) {
      return `Idle-lock must be between ${MIN_MINUTES} and ${MAX_MINUTES} minutes.`;
    }
    return "";
  }, [minutes]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(base, { headers: getHeaders() })
      .then((r) => {
        if (cancelled) return;
        const secs = Number(r.data?.pos_settings?.idle_lock_seconds) || 1800;
        setMinutes(Math.round(secs / 60));
        setSource(r.data?._source || null);
        setError("");
      })
      .catch((e) => {
        toast.error(`Could not load security settings: ${e?.response?.data?.detail || e.message}`);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [base, getHeaders]);

  const save = async () => {
    if (clientError) return;
    setSaving(true);
    setError("");
    try {
      const r = await axios.put(base, {
        idle_lock_seconds: Math.round(Number(minutes) * 60),
      }, { headers: getHeaders() });
      setSource(r.data?._source || "saved");
      toast.success("Idle-lock timeout saved.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const isDefault = source === "default";

  return (
    <Card className="border-[#E2E8DF]" data-testid="pos-security-settings-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-[color:var(--color-primary)]" />
              <h2 className="text-[18px] font-semibold text-[#1A2421]">Terminal Security</h2>
              {isDefault && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-widest border-amber-300 text-amber-800 bg-amber-50"
                       data-testid="pos-security-default-badge">
                  Using default
                </Badge>
              )}
              {!isDefault && source === "saved" && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-widest border-emerald-300 text-emerald-800 bg-emerald-50"
                       data-testid="pos-security-saved-badge">
                  Saved
                </Badge>
              )}
            </div>
            <p className="text-[13px] text-[#5C6B64] mt-1">
              {outletName ? `${outletName} — ` : ""}how long a staff PIN stays unlocked after the last touch or keypress.
            </p>
          </div>
        </div>

        {isDefault && (
          <div className="mb-4 p-3 rounded-md border border-amber-200 bg-amber-50 flex items-start gap-2 text-[12px] text-amber-900"
               data-testid="pos-security-default-hint">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              The terminal is using the default idle-lock timeout of 30 minutes. Adjust below to override.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-[12px]">Idle-lock timeout (minutes)</Label>
            <Input type="number" min={MIN_MINUTES} max={MAX_MINUTES} step={1}
                   value={minutes}
                   onChange={(e) => setMinutes(e.target.value)}
                   className="mt-1 tabular-nums"
                   data-testid="pos-security-idle-lock-minutes" />
            <p className="text-[11px] text-[#5C6B64] mt-1">
              Any activity (touch, keypress, click) resets the countdown. When it hits zero the terminal returns to the PIN screen; open orders, shifts and tills stay open.
            </p>
          </div>
        </div>

        {(clientError || error) && (
          <div className="mt-4 p-3 rounded-md border border-rose-200 bg-rose-50 text-[12px] text-rose-900"
               data-testid="pos-security-error">
            {clientError || error}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end">
          <Button onClick={save} disabled={!!clientError || saving}
                  data-testid="pos-security-save"
                  className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white">
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
